// src/services/api.ts - IMPROVED VERSION with better error handling
import axios, { AxiosResponse } from 'axios';
import {
  User,
  League,
  SeasonNow,
  Team,
  Player,
  PlayerSeasonStats,
  LoginCredentials,
  RegisterData,
  CreateLeagueData,
  RosterSlot,
  ScoringSystem,
  DraftStateResponse,
  PickResponse,
  StandingsResponse,
  Matchup,
  BoxscoreResponse,
  LeagueTransaction,
  InviteCodePreview,
  LeagueUpdate,
  Trade,
  ProposeTradeData,
  AppNotification,
  WaiverClaim,
  SubmitWaiverData,
  WeeklySplit,
  PlayerWeekPoints,
  BracketResponse,
  AdminOverview,
  AdminUser,
  AdminLeague,
  PublicLeaguePage,
} from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8002/api/v1';

// Custom error class for API errors
export class ApiError extends Error {
  status?: number;
  detail?: string;

  constructor(message: string, status?: number, detail?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
  }
}

// Helper function to handle API errors
export const handleApiError = (error: any): string => {
  console.error('API Error Details:', {
    message: error.message,
    response: error.response?.data,
    status: error.response?.status,
    config: {
      method: error.config?.method,
      url: error.config?.url,
      headers: error.config?.headers
    }
  });

  if (error.response) {
    // Server responded with error status
    const data = error.response.data;
    if (data.detail) {
      return typeof data.detail === 'string' ? data.detail : 'An error occurred';
    }
    return data.message || `HTTP ${error.response.status}: ${error.response.statusText}`;
  } else if (error.request) {
    // Request made but no response received - likely CORS or network issue
    if (error.code === 'ERR_NETWORK') {
      return 'Network error. Please check if the backend server is running on http://localhost:8002';
    }
    return 'Unable to connect to server. Please check your internet connection and ensure the backend is running.';
  } else {
    // Something else happened
    return error.message || 'An unexpected error occurred';
  }
};

// Axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000, // Increased timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token and log requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Log requests in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`🔄 ${config.method?.toUpperCase()} ${config.url}`, {
        headers: config.headers,
        data: config.data
      });
    }
    
    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor to handle common errors and log responses
api.interceptors.response.use(
  (response) => {
    // Log successful responses in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`✅ ${response.config.method?.toUpperCase()} ${response.config.url}`, {
        status: response.status,
        data: response.data
      });
    }
    return response;
  },
  (error) => {
    // Log errors in development
    if (process.env.NODE_ENV === 'development') {
      console.error(`❌ ${error.config?.method?.toUpperCase()} ${error.config?.url}`, {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
    }
    
    if (error.response?.status === 401) {
      // Unauthorized - clear token and redirect to login
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// API service methods
const apiService = {
  // Auth endpoints
  async login(credentials: LoginCredentials): Promise<{ user: User; token: string }> {
    try {
      // FastAPI expects form data for OAuth2PasswordRequestForm
      const formData = new URLSearchParams();
      formData.append('username', credentials.email); // Note: username field for email
      formData.append('password', credentials.password);
      
      const response: AxiosResponse<{ user: User; token: string }> = await api.post('/auth/login', formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });
      return response.data;
    } catch (error) {
      throw new ApiError(handleApiError(error));
    }
  },

  async register(data: RegisterData): Promise<{ user: User; token: string }> {
    try {
      const response: AxiosResponse<{ user: User; token: string }> = await api.post('/auth/register', data);
      return response.data;
    } catch (error) {
      throw new ApiError(handleApiError(error));
    }
  },

  async getCurrentUser(): Promise<User> {
    try {
      const response: AxiosResponse<User> = await api.get('/auth/me');
      return response.data;
    } catch (error) {
      throw new ApiError(handleApiError(error));
    }
  },

  async forgotPassword(email: string): Promise<{ message: string }> {
    try {
      const response: AxiosResponse<{ message: string }> = await api.post('/auth/forgot-password', { email });
      return response.data;
    } catch (error) {
      throw new ApiError(handleApiError(error));
    }
  },

  async resetPassword(token: string, newPassword: string): Promise<{ message: string }> {
    try {
      const response: AxiosResponse<{ message: string }> = await api.post('/auth/reset-password', {
        token,
        new_password: newPassword,
      });
      return response.data;
    } catch (error) {
      throw new ApiError(handleApiError(error));
    }
  },

  // Season — the masthead dateline's source. Public.
  async getCurrentSeason(): Promise<SeasonNow> {
    try {
      const response: AxiosResponse<SeasonNow> = await api.get('/season/current');
      return response.data;
    } catch (error) {
      throw new ApiError(handleApiError(error));
    }
  },

  // League endpoints
  async getLeagues(): Promise<League[]> {
    try {
      const response: AxiosResponse<League[]> = await api.get('/leagues/');
      return response.data;
    } catch (error) {
      throw new ApiError(handleApiError(error));
    }
  },

  async getLeague(id: number): Promise<League> {
    try {
      const response: AxiosResponse<League> = await api.get(`/leagues/${id}`);
      return response.data;
    } catch (error) {
      throw new ApiError(handleApiError(error));
    }
  },

  async createLeague(data: CreateLeagueData): Promise<League> {
    try {
      const response: AxiosResponse<League> = await api.post('/leagues/', data);
      return response.data;
    } catch (error) {
      throw new ApiError(handleApiError(error));
    }
  },

  // Team endpoints
  async getTeams(leagueId: number): Promise<Team[]> {
    try {
      const response: AxiosResponse<Team[]> = await api.get(`/teams/league/${leagueId}/teams`);
      return response.data;
    } catch (error) {
      throw new ApiError(handleApiError(error));
    }
  },

  async getStandings(leagueId: number): Promise<StandingsResponse> {
    try {
      const response: AxiosResponse<StandingsResponse> = await api.get(`/team-stats/${leagueId}/standings`);
      return response.data;
    } catch (error) {
      throw new ApiError(handleApiError(error));
    }
  },

  async getMatchups(leagueId: number, week?: number): Promise<Matchup[]> {
    try {
      const params = week !== undefined ? { week } : {};
      const response: AxiosResponse<{ matchups: Matchup[] }> = await api.get(`/matchups/${leagueId}`, { params });
      return response.data.matchups;
    } catch (error) {
      throw new ApiError(handleApiError(error));
    }
  },

  async generateSchedule(leagueId: number): Promise<{ message: string }> {
    try {
      const response: AxiosResponse<{ message: string }> = await api.post(`/matchups/${leagueId}/generate`);
      return response.data;
    } catch (error) {
      throw new ApiError(handleApiError(error));
    }
  },

  async simulateWeek(leagueId: number, week: number): Promise<{ message: string }> {
    try {
      const response: AxiosResponse<{ message: string }> = await api.post(`/matchups/${leagueId}/simulate-week/${week}`);
      return response.data;
    } catch (error) {
      throw new ApiError(handleApiError(error));
    }
  },

  async getBoxscore(matchupId: number): Promise<BoxscoreResponse> {
    try {
      const response: AxiosResponse<BoxscoreResponse> = await api.get(`/matchups/${matchupId}/boxscore`);
      return response.data;
    } catch (error) {
      throw new ApiError(handleApiError(error));
    }
  },

  async getAvailablePlayers(leagueId: number, position?: string, search?: string): Promise<Player[]> {
    try {
      const params: Record<string, string> = {};
      if (position && position !== 'all') params.position = position;
      if (search) params.search = search;
      const response: AxiosResponse<Player[]> = await api.get(`/leagues/${leagueId}/available-players`, { params });
      return response.data;
    } catch (error) {
      throw new ApiError(handleApiError(error));
    }
  },

  async swapPlayer(teamId: number, playerAddId: number, playerDropId?: number): Promise<{ message: string; added_player_id: number; dropped_player_id: number | null }> {
    try {
      const body: { player_add_id: number; player_drop_id?: number } = { player_add_id: playerAddId };
      if (playerDropId !== undefined) body.player_drop_id = playerDropId;
      const response = await api.post(`/roster/teams/${teamId}/swap`, body);
      return response.data;
    } catch (error) {
      throw new ApiError(handleApiError(error));
    }
  },

  async getTransactions(leagueId: number, limit = 20): Promise<LeagueTransaction[]> {
    try {
      const response: AxiosResponse<LeagueTransaction[]> = await api.get(`/leagues/${leagueId}/transactions`, { params: { limit } });
      return response.data;
    } catch (error) {
      throw new ApiError(handleApiError(error));
    }
  },

  async getTeam(id: number): Promise<Team> {
    try {
      const response: AxiosResponse<Team> = await api.get(`/teams/${id}`);
      return response.data;
    } catch (error) {
      throw new ApiError(handleApiError(error));
    }
  },

  async getTeamWeeklySplits(teamId: number): Promise<WeeklySplit[]> {
    try {
      const response: AxiosResponse<WeeklySplit[]> = await api.get(`/teams/${teamId}/weekly-splits`);
      return response.data;
    } catch (error) {
      throw new ApiError(handleApiError(error));
    }
  },

  async joinLeague(leagueId: number, teamName: string): Promise<Team> {
    try {
      const response: AxiosResponse<Team> = await api.post(`/teams/league/${leagueId}/join`, {
        team_name: teamName
      });
      return response.data;
    } catch (error) {
      throw new ApiError(handleApiError(error));
    }
  },

  async getInviteCodePreview(code: string): Promise<InviteCodePreview> {
    try {
      const response: AxiosResponse<InviteCodePreview> = await api.get(`/leagues/join/${code}`);
      return response.data;
    } catch (error) {
      throw new ApiError(handleApiError(error));
    }
  },

  async joinLeagueByCode(code: string, teamName: string): Promise<Team> {
    try {
      const response: AxiosResponse<Team> = await api.post(`/leagues/join/${code}`, {
        team_name: teamName,
      });
      return response.data;
    } catch (error) {
      throw new ApiError(handleApiError(error));
    }
  },

  async regenerateInviteCode(leagueId: number): Promise<{ invite_code: string }> {
    try {
      const response: AxiosResponse<{ invite_code: string }> = await api.post(
        `/leagues/${leagueId}/regenerate-invite-code`
      );
      return response.data;
    } catch (error) {
      throw new ApiError(handleApiError(error));
    }
  },

  async updateLeague(id: number, data: LeagueUpdate): Promise<League> {
    try {
      const response: AxiosResponse<League> = await api.put(`/leagues/${id}`, data);
      return response.data;
    } catch (error) {
      throw new ApiError(handleApiError(error));
    }
  },

  async deleteLeague(id: number): Promise<void> {
    try {
      await api.delete(`/leagues/${id}`);
    } catch (error) {
      throw new ApiError(handleApiError(error));
    }
  },

  async transferCommissioner(id: number, newCommissionerId: number): Promise<League> {
    try {
      const response: AxiosResponse<League> = await api.post(
        `/leagues/${id}/transfer-commissioner`,
        { new_commissioner_id: newCommissionerId },
      );
      return response.data;
    } catch (error) {
      throw new ApiError(handleApiError(error));
    }
  },

  // Playoff endpoints
  async getPlayoffBracket(leagueId: number): Promise<BracketResponse | null> {
    try {
      const response: AxiosResponse<BracketResponse> = await api.get(`/playoffs/${leagueId}`);
      return response.data;
    } catch (error: any) {
      if (error?.response?.status === 404) return null;  // no bracket yet — a state, not an error
      throw new ApiError(handleApiError(error));
    }
  },

  async generatePlayoffs(leagueId: number): Promise<BracketResponse> {
    try {
      const response: AxiosResponse<BracketResponse> = await api.post(`/playoffs/${leagueId}/generate`);
      return response.data;
    } catch (error) {
      throw new ApiError(handleApiError(error));
    }
  },

  async startPlayoffs(leagueId: number): Promise<BracketResponse> {
    try {
      const response: AxiosResponse<BracketResponse> = await api.post(`/playoffs/${leagueId}/start`);
      return response.data;
    } catch (error) {
      throw new ApiError(handleApiError(error));
    }
  },

  async finalizePlayoffRound(leagueId: number): Promise<BracketResponse> {
    try {
      const response: AxiosResponse<BracketResponse> = await api.post(`/playoffs/${leagueId}/finalize-round`);
      return response.data;
    } catch (error) {
      throw new ApiError(handleApiError(error));
    }
  },

  // Player endpoints
  async getPlayers(filters?: any): Promise<Player[]> {
    try {
      const params = new URLSearchParams();
      if (filters?.position && filters.position !== 'all') {
        params.append('position', filters.position);
      }
      if (filters?.search) {
        params.append('search', filters.search);
      }
      if (filters?.limit) {
        params.append('limit', filters.limit.toString());
      }
      
      const queryString = params.toString();
      const url = queryString ? `/players/?${queryString}` : '/players/';
      const response: AxiosResponse<Player[]> = await api.get(url);
      return response.data;
    } catch (error) {
      throw new ApiError(handleApiError(error));
    }
  },

  async getPlayerSeasonStats(playerId: number, limit = 5): Promise<PlayerSeasonStats[]> {
    try {
      const response: AxiosResponse<PlayerSeasonStats[]> = await api.get(
        `/players/${playerId}/season-stats?limit=${limit}`
      );
      return response.data;
    } catch (error) {
      throw new ApiError(handleApiError(error));
    }
  },

  async getPlayerWeeklyStats(playerId: number, season: number): Promise<PlayerWeekPoints[]> {
    try {
      const response: AxiosResponse<PlayerWeekPoints[]> = await api.get(`/players/${playerId}/weekly-stats`, { params: { season } });
      return response.data;
    } catch (error) {
      throw new ApiError(handleApiError(error));
    }
  },

  // Roster endpoints with improved error handling
  async getRoster(teamId: number): Promise<RosterSlot[]> {
    try {
      const response: AxiosResponse<RosterSlot[]> = await api.get(`/roster/teams/${teamId}/roster`);
      return response.data;
    } catch (error) {
      throw new ApiError(handleApiError(error));
    }
  },

  async addPlayerToRoster(teamId: number, playerId: number): Promise<RosterSlot> {
    try {
      console.log(`🔄 Adding player ${playerId} to team ${teamId} roster...`);
      const response: AxiosResponse<RosterSlot> = await api.post(`/roster/teams/${teamId}/roster/add/${playerId}`);
      console.log(`✅ Successfully added player to roster:`, response.data);
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to add player ${playerId} to team ${teamId}:`, error);
      throw new ApiError(handleApiError(error));
    }
  },

  async updateStarterStatus(teamId: number, playerId: number, isStarter: boolean): Promise<RosterSlot> {
    try {
      const response: AxiosResponse<RosterSlot> = await api.patch(
        `/roster/teams/${teamId}/roster/${playerId}`,
        { is_starter: isStarter }
      );
      return response.data;
    } catch (error) {
      throw new ApiError(handleApiError(error));
    }
  },

  async removePlayerFromRoster(teamId: number, playerId: number): Promise<void> {
    try {
      console.log(`🔄 Removing player ${playerId} from team ${teamId} roster...`);
      await api.delete(`/roster/teams/${teamId}/roster/${playerId}`);
      console.log(`✅ Successfully removed player from roster`);
    } catch (error) {
      console.error(`❌ Failed to remove player ${playerId} from team ${teamId}:`, error);
      throw new ApiError(handleApiError(error));
    }
  },

  // NFL Teams
  async getNFLTeams(): Promise<any[]> {
    try {
      const response: AxiosResponse<any[]> = await api.get('/nfl-teams/');
      return response.data;
    } catch (error) {
      throw new ApiError(handleApiError(error));
    }
  },

  async getScoringSystems(): Promise<ScoringSystem[]> {
    try {
      const response: AxiosResponse<ScoringSystem[]> = await api.get('/scoring-systems/');
      return response.data;
    } catch (error) {
      throw new ApiError(handleApiError(error));
    }
  },

  // Health check endpoint
  async healthCheck(): Promise<{ status: string; message: string }> {
    try {
      const response: AxiosResponse<{ status: string; message: string }> = await api.get('/');
      return response.data;
    } catch (error) {
      throw new ApiError(handleApiError(error));
    }
  },
// Roster Update endpoints
async getRosterUpdateStatus(): Promise<any> {
  try {
    const response = await api.get('/roster-updates/status');
    return response.data;
  } catch (error) {
    throw new ApiError(handleApiError(error));
  }
},

async triggerManualRosterUpdate(): Promise<any> {
  try {
    const response = await api.post('/roster-updates/manual');
    return response.data;
  } catch (error) {
    throw new ApiError(handleApiError(error));
  }
},

async startBackgroundRosterUpdates(): Promise<any> {
  try {
    const response = await api.post('/roster-updates/start-background');
    return response.data;
  } catch (error) {
    throw new ApiError(handleApiError(error));
  }
},

async stopBackgroundRosterUpdates(): Promise<any> {
  try {
    const response = await api.post('/roster-updates/stop-background');
    return response.data;
  } catch (error) {
    throw new ApiError(handleApiError(error));
  }
},

async getRecentRosterChanges(hours: number = 24): Promise<any> {
  try {
    const response = await api.get(`/roster-updates/recent-changes?hours=${hours}`);
    return response.data;
  } catch (error) {
    throw new ApiError(handleApiError(error));
  }
},

async getTeamRosterFromAPI(teamAbbreviation: string): Promise<any> {
  try {
    const response = await api.get(`/roster-updates/team/${teamAbbreviation}`);
    return response.data;
  } catch (error) {
    throw new ApiError(handleApiError(error));
  }
},

  // Draft endpoints
  async startDraft(leagueId: number): Promise<DraftStateResponse> {
    try {
      const response: AxiosResponse<DraftStateResponse> = await api.post(`/draft/${leagueId}/start`);
      return response.data;
    } catch (error) {
      throw new ApiError(handleApiError(error));
    }
  },

  async getDraftState(leagueId: number): Promise<DraftStateResponse> {
    try {
      const response: AxiosResponse<DraftStateResponse> = await api.get(`/draft/${leagueId}/state`);
      return response.data;
    } catch (error) {
      throw new ApiError(handleApiError(error));
    }
  },

  async makePick(leagueId: number, teamId: number, playerId: number): Promise<PickResponse> {
    try {
      const response: AxiosResponse<PickResponse> = await api.post(`/draft/${leagueId}/pick`, {
        team_id: teamId,
        player_id: playerId,
      });
      return response.data;
    } catch (error) {
      throw new ApiError(handleApiError(error));
    }
  },

  async autoPick(leagueId: number, teamId: number): Promise<PickResponse> {
    try {
      const response: AxiosResponse<PickResponse> = await api.post(`/draft/${leagueId}/auto-pick`, {
        team_id: teamId,
      });
      return response.data;
    } catch (error) {
      throw new ApiError(handleApiError(error));
    }
  },

  // Trade endpoints
  async getLeagueTrades(leagueId: number): Promise<Trade[]> {
    try {
      const response: AxiosResponse<Trade[]> = await api.get(`/trades/leagues/${leagueId}`);
      return response.data;
    } catch (error) {
      throw new ApiError(handleApiError(error));
    }
  },

  async proposeTrade(leagueId: number, data: ProposeTradeData): Promise<Trade> {
    try {
      const response: AxiosResponse<Trade> = await api.post(`/trades/leagues/${leagueId}`, data);
      return response.data;
    } catch (error) {
      throw new ApiError(handleApiError(error));
    }
  },

  async acceptTrade(tradeId: number): Promise<Trade> {
    try {
      const response: AxiosResponse<Trade> = await api.post(`/trades/${tradeId}/accept`);
      return response.data;
    } catch (error) {
      throw new ApiError(handleApiError(error));
    }
  },

  async rejectTrade(tradeId: number): Promise<Trade> {
    try {
      const response: AxiosResponse<Trade> = await api.post(`/trades/${tradeId}/reject`);
      return response.data;
    } catch (error) {
      throw new ApiError(handleApiError(error));
    }
  },

  async cancelTrade(tradeId: number): Promise<Trade> {
    try {
      const response: AxiosResponse<Trade> = await api.post(`/trades/${tradeId}/cancel`);
      return response.data;
    } catch (error) {
      throw new ApiError(handleApiError(error));
    }
  },

  async vetoTrade(tradeId: number): Promise<Trade> {
    try {
      const response: AxiosResponse<Trade> = await api.post(`/trades/${tradeId}/veto`);
      return response.data;
    } catch (error) {
      throw new ApiError(handleApiError(error));
    }
  },

  // Notification endpoints
  async getNotifications(params?: { unread_only?: boolean; limit?: number; offset?: number }): Promise<AppNotification[]> {
    try {
      const response: AxiosResponse<AppNotification[]> = await api.get('/notifications/', { params });
      return response.data;
    } catch (error) {
      throw new ApiError(handleApiError(error));
    }
  },

  async getUnreadNotificationCount(): Promise<number> {
    try {
      const response: AxiosResponse<{ count: number }> = await api.get('/notifications/unread-count');
      return response.data.count;
    } catch (error) {
      throw new ApiError(handleApiError(error));
    }
  },

  async markNotificationRead(id: number): Promise<AppNotification> {
    try {
      const response: AxiosResponse<AppNotification> = await api.put(`/notifications/${id}/mark-read`);
      return response.data;
    } catch (error) {
      throw new ApiError(handleApiError(error));
    }
  },

  async markAllNotificationsRead(): Promise<void> {
    try {
      await api.put('/notifications/mark-all-read');
    } catch (error) {
      throw new ApiError(handleApiError(error));
    }
  },

  async deleteNotification(id: number): Promise<void> {
    try {
      await api.delete(`/notifications/${id}`);
    } catch (error) {
      throw new ApiError(handleApiError(error));
    }
  },

  // Waiver endpoints
  async getWaiverAvailablePlayers(leagueId: number, params?: { position?: string; search?: string }): Promise<Player[]> {
    try {
      const response: AxiosResponse<Player[]> = await api.get(`/leagues/${leagueId}/available-players`, { params });
      return response.data;
    } catch (error) { throw new ApiError(handleApiError(error)); }
  },
  async getMyWaiverClaims(leagueId: number): Promise<WaiverClaim[]> {
    try {
      const response: AxiosResponse<WaiverClaim[]> = await api.get(`/leagues/${leagueId}/waiver-claims`);
      return response.data;
    } catch (error) { throw new ApiError(handleApiError(error)); }
  },
  async submitWaiverClaim(leagueId: number, data: SubmitWaiverData): Promise<{ claim_id: number }> {
    try {
      const response = await api.post(`/leagues/${leagueId}/waiver-claims`, data);
      return response.data;
    } catch (error) { throw new ApiError(handleApiError(error)); }
  },
  async cancelWaiverClaim(claimId: number): Promise<void> {
    try { await api.delete(`/waiver-claims/${claimId}`); }
    catch (error) { throw new ApiError(handleApiError(error)); }
  },
  async reorderWaiverClaim(claimId: number, direction: 'up' | 'down'): Promise<void> {
    try { await api.patch(`/waiver-claims/${claimId}/reorder`, { direction }); }
    catch (error) { throw new ApiError(handleApiError(error)); }
  },
  async processWaivers(leagueId: number): Promise<{ processed: number; denied: number; message: string }> {
    try {
      const response = await api.post(`/leagues/${leagueId}/process-waivers`);
      return response.data;
    } catch (error) { throw new ApiError(handleApiError(error)); }
  },

  // Admin endpoints
  async getAdminOverview(): Promise<AdminOverview> {
    try {
      const response: AxiosResponse<AdminOverview> = await api.get('/admin/overview');
      return response.data;
    } catch (error) {
      throw new ApiError(handleApiError(error));
    }
  },

  async getAdminUsers(): Promise<AdminUser[]> {
    try {
      const response: AxiosResponse<AdminUser[]> = await api.get('/admin/users');
      return response.data;
    } catch (error) {
      throw new ApiError(handleApiError(error));
    }
  },

  async setUserActive(id: number, is_active: boolean): Promise<{ id: number; is_active: boolean }> {
    try {
      const response: AxiosResponse<{ id: number; is_active: boolean }> = await api.patch(`/admin/users/${id}/active`, { is_active });
      return response.data;
    } catch (error) {
      throw new ApiError(handleApiError(error));
    }
  },

  async setUserSuperuser(id: number, is_superuser: boolean): Promise<{ id: number; is_superuser: boolean }> {
    try {
      const response: AxiosResponse<{ id: number; is_superuser: boolean }> = await api.patch(`/admin/users/${id}/superuser`, { is_superuser });
      return response.data;
    } catch (error) {
      throw new ApiError(handleApiError(error));
    }
  },

  async deleteUser(id: number): Promise<{ deleted: number }> {
    try {
      const response: AxiosResponse<{ deleted: number }> = await api.delete(`/admin/users/${id}`);
      return response.data;
    } catch (error) {
      throw new ApiError(handleApiError(error));
    }
  },

  async discoverPublicLeagues(params: { search?: string; skip?: number; limit?: number } = {}): Promise<PublicLeaguePage> {
    try {
      const response: AxiosResponse<PublicLeaguePage> = await api.get('/leagues/public/discover', { params });
      return response.data;
    } catch (error) {
      throw new ApiError(handleApiError(error));
    }
  },

  async getAdminLeagues(): Promise<AdminLeague[]> {
    try {
      const response: AxiosResponse<AdminLeague[]> = await api.get('/admin/leagues');
      return response.data;
    } catch (error) {
      throw new ApiError(handleApiError(error));
    }
  },

  async deleteAdminLeague(id: number): Promise<{ deleted: number }> {
    try {
      const response: AxiosResponse<{ deleted: number }> = await api.delete(`/admin/leagues/${id}`);
      return response.data;
    } catch (error) {
      throw new ApiError(handleApiError(error));
    }
  },

  async getPlaceholderStatsCount(): Promise<{ count: number }> {
    try {
      const response: AxiosResponse<{ count: number }> = await api.get('/admin/stats/placeholder');
      return response.data;
    } catch (error) {
      throw new ApiError(handleApiError(error));
    }
  },

  async purgePlaceholderStats(): Promise<{ deleted: number }> {
    try {
      const response: AxiosResponse<{ deleted: number }> = await api.delete('/admin/stats/placeholder');
      return response.data;
    } catch (error) {
      throw new ApiError(handleApiError(error));
    }
  },

  async ingestStats(season: number, week?: number): Promise<any> {
    try {
      const body: { season: number; week?: number } = { season };
      if (week !== undefined) body.week = week;
      const response: AxiosResponse<any> = await api.post('/admin/stats/ingest', body);
      return response.data;
    } catch (error) {
      throw new ApiError(handleApiError(error));
    }
  },

  async buildIdMap(season: number): Promise<any> {
    try {
      const response: AxiosResponse<any> = await api.post('/admin/stats/build-id-map', { season });
      return response.data;
    } catch (error) {
      throw new ApiError(handleApiError(error));
    }
  },
};

export default apiService;